// web_components/wcAccordion/wcAccordion.ts

import { WebComponent_Base } from '../WebComponent_Base';
import { WebComponent_Utils } from '../WebComponent_Utils';
import { renderProductHTML } from './wcAccordion_Renderer';

/**
 * A custom web component for displaying an Accordion section.
 */
export class WcAccordion extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcAccordion';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error(
					'wcAccordion Component: Invalid JSON in data-variables attribute:',
					e,
				);
				this.dataAttributes['variables'] = null;
			}
		}

		// CSS
		const css = this.getAttribute('data-css');
		if (css) {
			try {
				this.dataAttributes['css'] = JSON.parse(css);
			} catch (e) {
				console.error('wcAccordion Component: Invalid CSS in data-css attribute:', e);
				this.dataAttributes['css'] = null;
			}
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-accordion', WcAccordion);
